/**
 * Public types for the rm-playlist-v2 widget bundle.
 *
 * `PublicPlay` and `LiveEventSummary` mirror the shapes returned by the
 * Convex public queries (`plays.currentByStation`, `plays.recentByStation`).
 * Duplicated here rather than imported from `@rm/convex` so the embed
 * bundle stays free of Convex's server-side codegen — the bundle uses
 * `@rm/convex/api` at runtime for typed query references, but the shapes
 * below are what arrives at the component boundary.
 */

export type StationSlug = "hyfin" | "88nine" | "414music" | "rhythmlab";

export type WidgetVariant = "playlist" | "now-playing-card" | "now-playing-strip";

export type WidgetTheme = "auto" | "light" | "dark";

export type WidgetLayout = "list" | "grid";

export interface WidgetConfig {
  readonly station: StationSlug;
  readonly variant: WidgetVariant;
  readonly layout?: WidgetLayout;
  readonly theme?: WidgetTheme;
  readonly maxItems?: number;
  readonly showSearch?: boolean;
  readonly showHeader?: boolean;
  readonly showLoadMore?: boolean;
  /**
   * Render the "Powered by Radio Milwaukee" footer line at the bottom of
   * the playlist widget. Default `false` so partner-site embeds stay native
   * to the host page. Opt in (`data-show-footer="true"`) when the host
   * wants the attribution chip — typically RM-owned pages.
   *
   * When shown, the footer renders in the host-inherited body font at
   * normal case, not mono uppercase. PRODUCT.md anti-references forbid
   * "'Powered by' footers in a different visual language than the host."
   */
  readonly showFooter?: boolean;
  readonly enablePreview?: boolean;
  readonly enableDateSearch?: boolean;
  /**
   * When `false`, the widget renders a one-shot snapshot instead of a live
   * subscription. Default `true` — matches V1 default + most embed cases.
   */
  readonly autoUpdate?: boolean;
  /**
   * Reserved for chunk 4 — when `true`, removes the 100-row cap and uses
   * a cursor-paginated query. Today still capped at 100; the attr is
   * accepted but has no extra effect. See playlist.tsx PAGE_CEILING.
   */
  readonly unlimitedSongs?: boolean;
}

/**
 * LIVE event slot on a play row — the product differentiator (DESIGN.md
 * § B tertiary tier). Populated by `findLiveEventForArtist` in
 * packages/convex/convex/plays.ts: when the play's artist has an upcoming
 * non-duplicate, non-cancelled event in the station's region, the
 * soonest one is returned here. `null` otherwise. Mirrors the server
 * `LiveEventSummary` interface (Id<"events"> serializes to string).
 */
export interface LiveEventSummary {
  readonly eventId: string;
  /** Show title as the source reported it. May be null when the source
   *  omitted it; UI falls back to "Live" + venue. */
  readonly title: string | null;
  /** The matched performer's name. */
  readonly artistName: string;
  /** Headliner vs support — drives the "X opens for Y" framing in
   *  LiveEventRow so the listener understands the connection between
   *  the song they just heard and the show being announced. */
  readonly role: "headliner" | "support";
  readonly venue: string;
  readonly city: string;
  readonly startsAtMs: number;
  readonly ticketUrl: string | null;
  /** Event poster / hero image. Shown as thumbnail in the row and full-width in the modal. */
  readonly imageUrl: string | null;
  /** Upstream ticketing source — drives brand styling (AXS Blue vs amber). */
  readonly source: "ticketmaster" | "axs" | "custom";
  /** True when the source has no specific time; display date only. */
  readonly dateOnly: boolean;
  /** Door-open time in ms. */
  readonly doorsAt: number | null;
  /** Genre label from the source (e.g. "Alternative"). */
  readonly genre: string | null;
  /** All headlining acts on this event. */
  readonly headliners: readonly string[];
  /** All support acts on this event. */
  readonly supports: readonly string[];
}

export interface PublicPlay {
  readonly _id: string;
  readonly artistRaw: string;
  readonly titleRaw: string;
  readonly albumRaw: string | undefined;
  readonly playedAt: number;
  readonly artist: string;
  readonly title: string;
  readonly album: string | null;
  readonly label: string | null;
  readonly durationSec: number | null;
  readonly artworkUrl: string | null;
  readonly spotifyTrackId: string | null;
  readonly appleMusicSongId: string | null;
  readonly previewUrl: string | null;
  readonly liveEvent: LiveEventSummary | null;
}

/**
 * A `PublicPlay` extended with a play-count, returned by
 * `plays.topSongsByStation` for the Top 20 tabs.
 */
export interface PublicTopSong extends PublicPlay {
  readonly playCount: number;
}

export const STATION_LABEL: Record<StationSlug, string> = {
  hyfin: "HYFIN",
  "88nine": "88Nine",
  "414music": "414 Music",
  rhythmlab: "Rhythm Lab",
};
